import React, { useCallback, useEffect, useState } from "react";
import { logError } from "@/lib/logger";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Brand from "@/components/Brand";
import {
    Lightning,
    ArrowRight,
    CheckCircle,
    Sparkle,
    UploadSimple,
    MapPin,
    Briefcase,
} from "@phosphor-icons/react";
import AutocompleteInput from "@/components/AutocompleteInput";
import SkillsChipInput from "@/components/SkillsChipInput";
import { smartPasteHandler, reformatPastedText } from "@/lib/smartPaste";
import { LOCATION_SUGGESTIONS } from "@/lib/suggestions";

export default function PublicApply() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [params] = useSearchParams();
    // Batch 3 (Feb 2026) — referral code from a friend's share link.
    // Preserves bounty attribution when the referred visitor signs up.
    const refCode = (params.get("ref") || "").trim().toUpperCase().slice(0, 12) || null;
    const [job, setJob] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [closed, setClosed] = useState(null); // {title, similar_jobs} when role is closed
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        current_title: "",
        location: "",
        years_experience: "",
        resume_text: "",
        linkedin: "",
    });
    const [skills, setSkills] = useState([]);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState("");
    const [done, setDone] = useState(false);
    // V2.0.52 (Feb 26, 2026) — id of the raw PDF stashed in R2 by
    // /public/resumes/parse. Sent back to /public/jobs/{id}/apply so
    // the actual file is attached to the candidate — recruiter no
    // longer sees "NO CV" after a public apply.
    const [uploadId, setUploadId] = useState(null);
    const [uploadedFilename, setUploadedFilename] = useState("");
    // V2.0.52 — already-applied short circuit. If a signed-in candidate
    // (or an anon visitor whose email we can guess from localStorage)
    // has already applied, we render an "all set" panel instead of the
    // form so they don't refill everything only to hit the 409.
    const [alreadyApplied, setAlreadyApplied] = useState(null);

    // Signup-first funnel — if this apply page was hit via a referral link and
    // the visitor isn't signed in as a candidate, funnel them through signup
    // FIRST (per user spec Feb 2026). After signup, CandidateSignup.jsx
    // deep-links back to `/apply/{id}`. Note: AuthContext uses `null`=loading,
    // `false`=anon, object=user — wait until we know the state.
    useEffect(() => {
        if (!refCode) return;
        if (user === null) return; // still loading
        if (user && user.role === "candidate") return; // already signed in as a candidate
        // Anonymous OR staff visitor with a referral link → funnel to signup.
        navigate(
            `/candidate/signup?job=${id}&ref=${encodeURIComponent(refCode)}`,
            { replace: true },
        );
    }, [refCode, user, id, navigate]);

    const loadJob = useCallback(() => {
        // Use bare fetch (no withCredentials, no auth) so the anon endpoint stays anon.
        fetch(`${API}/public/jobs/${id}`)
            .then(async (r) => {
                if (!r.ok) {
                    setNotFound(true);
                    return null;
                }
                return r.json();
            })
            .then((d) => {
                if (!d) return;
                if (d.status_flag === "closed") {
                    setClosed({
                        title: d.title,
                        similar_jobs: d.similar_jobs || [],
                    });
                } else {
                    setJob(d);
                }
            })
            .catch((e) => {
                logError("Public job load failed:", e);
                setNotFound(true);
            });
    }, [id]);

    useEffect(() => {
        loadJob();
    }, [loadJob]);

    // V2.0.52 (Feb 26, 2026) — prefill the form from a signed-in
    // candidate's profile so they can apply in 2 taps. Fetches
    // silently; any failure just keeps the form empty (existing UX).
    useEffect(() => {
        if (!user || user.role !== "candidate") return;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/me/profile");
                if (cancelled) return;
                setForm((f) => ({
                    ...f,
                    name: f.name || data.name || "",
                    email: f.email || data.email || "",
                    phone: f.phone || data.phone || "",
                    current_title: f.current_title || data.current_title || "",
                    location: f.location || data.location || "",
                    years_experience: f.years_experience || (data.years_experience ?? ""),
                    linkedin: f.linkedin || data.linkedin || "",
                    resume_text: f.resume_text || data.resume_text || "",
                }));
                if (Array.isArray(data.skills) && data.skills.length && skills.length === 0) {
                    setSkills(data.skills.slice(0, 30));
                }
            } catch {
                /* best-effort — form stays empty */
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    // V2.0.52 — detect already-applied upfront. Runs when we have a
    // signed-in candidate's email, so we can short-circuit the form
    // with a friendly "You've already applied — check status →" panel
    // instead of the current "fill everything → 409" flow.
    useEffect(() => {
        const emailToCheck = (user?.email || form.email || "").trim().toLowerCase();
        if (!emailToCheck || !id) return;
        let cancelled = false;
        const t = setTimeout(async () => {
            try {
                const r = await fetch(
                    `${API}/public/jobs/${id}/check-applied?email=${encodeURIComponent(emailToCheck)}`
                );
                if (!r.ok) return;
                const d = await r.json();
                if (cancelled) return;
                if (d.already_applied) setAlreadyApplied(d);
            } catch { /* best-effort */ }
        }, 350); // debounce so typing an email doesn't spam the endpoint
        return () => { cancelled = true; clearTimeout(t); };
    }, [id, user?.email, form.email]);

    const handlePdf = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setErr("");
        setPdfLoading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await fetch(`${API}/public/resumes/parse`, { method: "POST", body: fd });
            const data = await r.json();
            if (!r.ok) throw new Error(formatApiError(data.detail));
            const parsedText = reformatPastedText(data.text || "");
            // V2.0.52 — block silently-empty CVs (image-only PDFs where
            // OCR yielded nothing). Better to surface a helpful error
            // upfront than have the recruiter receive a candidate with
            // "NO CV" in the pipeline.
            if (parsedText.trim().length < 30) {
                throw new Error(
                    "We couldn't read enough text from your CV — it may be an image-only PDF. " +
                    "Please paste the resume text below, or upload a different file."
                );
            }
            setForm((f) => ({ ...f, resume_text: parsedText }));
            setUploadId(data.upload_id || null);
            setUploadedFilename(data.filename || file.name || "");
        } catch (e) {
            setErr(e.message || "Could not parse PDF");
        } finally {
            setPdfLoading(false);
            e.target.value = "";
        }
    };

    const submit = async (ev) => {
        ev.preventDefault();
        setErr("");
        setSubmitting(true);
        try {
            // Feb 18, 2026 — Prod bug: candidates applying via the public link
            // (`/apply/{id}`) while ALREADY LOGGED IN as a candidate had their
            // application stored with `candidate_user_id=null` because we
            // hit the anonymous `/public/jobs/{id}/apply` endpoint. Result:
            // the application never appeared in "Browse my role" / "My
            // applications" on the candidate portal.
            //
            // Fix: when the visitor is authenticated as a candidate, route
            // through the *authenticated* endpoint which uses their stored
            // profile and stamps `candidate_user_id=user.id` deterministically.
            if (user && user.role === "candidate") {
                try {
                    const { data } = await api.post(`/me/jobs/${id}/apply`);
                    void data;
                    try {
                        const { fireGadsConversion } = await import("@/lib/gtagConversions");
                        fireGadsConversion("job_application", { value: 100, currency: "INR" });
                    } catch { /* best-effort */ }
                    // V2.0.59 — close the share funnel loop.
                    try {
                        const raw = sessionStorage.getItem(`share_attr:${id}`);
                        if (raw) {
                            const attr = JSON.parse(raw);
                            api.post("/share/events", {
                                event: "apply", job_id: id,
                                channel: attr.channel || "unknown",
                                share_id: attr.share_id || null,
                            }).catch(() => {});
                        }
                    } catch { /* silent */ }
                    setDone(true);
                    return;
                } catch (ex) {
                    // Fall through to the anonymous path only if the
                    // authenticated call rejected for a reason unrelated to
                    // auth (e.g. missing resume in profile). Show a helpful
                    // error either way.
                    const detail = ex?.response?.data?.detail;
                    const msg = formatApiError(detail);
                    if (msg) throw new Error(msg);
                    throw ex;
                }
            }
            const payload = {
                ...form,
                years_experience: Number(form.years_experience) || 0,
                skills: skills,
                // V2.0.52 — hand the parse-time upload id to the backend
                // so the raw PDF is attached to the created candidate.
                upload_id: uploadId || undefined,
            };
            const r = await fetch(`${API}/public/jobs/${id}/apply`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            const data = await r.json();
            if (!r.ok) throw new Error(formatApiError(data.detail));
            try {
                const { fireGadsConversion } = await import("@/lib/gtagConversions");
                fireGadsConversion("job_application", { value: 100, currency: "INR" });
            } catch { /* best-effort */ }
            // V2.0.59 — close the share funnel loop (anonymous path).
            try {
                const raw = sessionStorage.getItem(`share_attr:${id}`);
                if (raw) {
                    const attr = JSON.parse(raw);
                    api.post("/share/events", {
                        event: "apply", job_id: id,
                        channel: attr.channel || "unknown",
                        share_id: attr.share_id || null,
                    }).catch(() => {});
                }
            } catch { /* silent */ }
            setDone(true);
        } catch (e) {
            setErr(e.message || "Could not submit application");
        } finally {
            setSubmitting(false);
        }
    };

    if (notFound) {
        return (
            <div className="min-h-screen bg-paper flex items-center justify-center px-4" data-testid="apply-not-found">
                <div className="bg-white border border-[#E5E5E5] p-10 max-w-md text-center">
                    <p className="font-heading font-black text-3xl tracking-tighter mb-2">
                        Role unavailable.
                    </p>
                    <p className="text-[#525252] mb-6">
                        This requirement is no longer accepting applications.
                    </p>
                    <Link
                        to="/"
                        className="text-xs font-bold uppercase tracking-wider text-cobalt"
                    >
                        Back to NaukriDaddy →
                    </Link>
                </div>
            </div>
        );
    }

    // Batch 3 (Feb 2026) — role Closed/Paused/Inactive: show similar-jobs UI.
    if (closed) {
        return (
            <div className="min-h-screen bg-paper px-4 py-10" data-testid="apply-closed-page">
                <div className="max-w-3xl mx-auto">
                    <Link to="/" className="inline-block mb-6">
                        <Brand size="h-8" to={null} />
                    </Link>
                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#A3A3A3] mb-2">
                        Role no longer active
                    </p>
                    <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter mb-3">
                        {closed.title || "This requirement was closed"}
                    </h1>
                    <p className="text-[#525252] mb-8">
                        Applications for this position are no longer being accepted —
                        but here are a few active roles that look similar.
                    </p>
                    {closed.similar_jobs.length === 0 ? (
                        <div className="bg-white border border-[#E5E5E5] p-6 text-sm text-[#525252]">
                            No similar open roles right now. Head to the{" "}
                            <Link to="/jobs" className="text-cobalt font-bold">
                                full board
                            </Link>{" "}
                            or create a candidate profile and we&apos;ll ping you when a match appears.
                        </div>
                    ) : (
                        <div className="grid sm:grid-cols-2 gap-3" data-testid="apply-similar-jobs">
                            {closed.similar_jobs.map((s) => (
                                <Link
                                    key={s.id}
                                    to={`/jobs/${s.id}${refCode ? `?ref=${encodeURIComponent(refCode)}` : ""}`}
                                    data-testid={`apply-similar-${s.id}`}
                                    className="bg-white border border-[#E5E5E5] hover:border-ink p-5 transition-colors"
                                >
                                    <p className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3] mb-1">
                                        {s.client?.name || s.department || "General"}
                                    </p>
                                    <h3 className="font-heading text-lg font-bold tracking-tight mb-2">
                                        {s.title}
                                    </h3>
                                    <div className="flex flex-wrap gap-2 text-[11px] text-[#525252]">
                                        {s.location && (
                                            <span className="inline-flex items-center gap-1">
                                                <MapPin size={10} weight="bold" /> {s.location}
                                            </span>
                                        )}
                                        {s.salary_range && (
                                            <span className="font-bold text-ink">
                                                · {s.salary_range}
                                            </span>
                                        )}
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    if (!job) {
        return (
            <div className="min-h-screen flex items-center justify-center text-[#525252]">
                Loading…
            </div>
        );
    }

    // V2.0.52 — already-applied short circuit. Rendered above the form
    // so a returning candidate sees a clear "you've applied" panel with
    // a sign-in CTA instead of the empty form (which they'd have filled
    // just to hit a 409). Suppressed while `done` (post-submit success)
    // so we don't stomp on the confetti state.
    if (alreadyApplied && !done) {
        return (
            <div
                className="min-h-screen bg-paper flex items-center justify-center px-4"
                data-testid="apply-already-applied"
            >
                <div className="bg-white border border-[#E5E5E5] p-8 sm:p-12 max-w-lg w-full text-center">
                    <CheckCircle
                        size={48}
                        weight="fill"
                        className="mx-auto text-cobalt mb-4"
                    />
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">
                        You&apos;ve already applied
                    </p>
                    <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter mb-3">
                        We&apos;ve got your profile.
                    </h1>
                    <p className="text-[#525252] mb-6">
                        Your application for{" "}
                        <span className="font-bold text-ink">{job?.title}</span>{" "}
                        is currently{" "}
                        <span className="font-bold text-ink">
                            {alreadyApplied.stage || "under review"}
                        </span>. The recruiter will reach out at{" "}
                        <span className="font-bold text-ink">
                            {user?.email || form.email}
                        </span>
                        {" "}when it moves forward.
                    </p>
                    {user?.role === "candidate" ? (
                        <Link
                            to="/candidate/applications"
                            data-testid="already-applied-track-btn"
                            className="inline-flex items-center gap-2 bg-ink text-white px-5 py-3 font-black uppercase tracking-wider text-xs hover:bg-cobalt transition-colors"
                        >
                            Track my applications <ArrowRight size={14} weight="bold" />
                        </Link>
                    ) : alreadyApplied.linked_to_account ? (
                        <Link
                            to="/candidate/login"
                            data-testid="already-applied-signin-btn"
                            className="inline-flex items-center gap-2 bg-ink text-white px-5 py-3 font-black uppercase tracking-wider text-xs hover:bg-cobalt transition-colors"
                        >
                            Sign in to track <ArrowRight size={14} weight="bold" />
                        </Link>
                    ) : (
                        <p className="text-xs text-[#A3A3A3]">
                            Check your email — we sent you a one-tap sign-in link
                            when you applied.
                        </p>
                    )}
                    <button
                        type="button"
                        onClick={() => setAlreadyApplied(null)}
                        data-testid="already-applied-dismiss"
                        className="block mx-auto mt-4 text-[10px] font-black uppercase tracking-wider text-[#A3A3A3] hover:text-ink"
                    >
                        Apply with a different email
                    </button>
                </div>
            </div>
        );
    }

    if (done) {
        return (
            <div
                className="min-h-screen bg-paper flex items-center justify-center px-4"
                data-testid="apply-success"
            >
                <div className="bg-white border border-[#E5E5E5] p-8 sm:p-12 max-w-lg w-full text-center">
                    <CheckCircle
                        size={48}
                        weight="fill"
                        className="mx-auto text-[#059669] mb-4"
                    />
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">
                        Application received
                    </p>
                    <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter mb-3">
                        You&apos;re in the pipeline.
                    </h1>
                    <p className="text-[#525252] mb-6">
                        Thanks for applying to <span className="font-bold text-ink">{job.title}</span>.
                        Our team will review your profile and reach out at{" "}
                        <span className="font-bold text-ink">{form.email}</span>.
                    </p>
                    <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-cobalt">
                        <Sparkle size={12} weight="fill" /> AI is scoring your match now
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-paper" data-testid="apply-page">
            {/* Top bar */}
            <div className="border-b border-[#E5E5E5] bg-white">
                <div className="max-w-5xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
                    <Brand size="h-7" />
                    <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3]">
                        Careers
                    </span>
                </div>
            </div>

            <div className="max-w-5xl mx-auto px-4 sm:px-8 py-8 sm:py-12 grid grid-cols-1 lg:grid-cols-5 gap-8">
                {/* JD side */}
                <div className="lg:col-span-2 lg:sticky lg:top-8 self-start">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">
                        {job.department || "Open role"}
                    </p>
                    <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter mb-4 text-ink">
                        {job.title}
                    </h1>
                    <div className="flex flex-wrap gap-2 mb-6">
                        <span className="text-[10px] font-black uppercase tracking-wider px-2 py-1 bg-[#F5F5F5] text-[#525252] inline-flex items-center gap-1">
                            <MapPin size={10} weight="bold" /> {job.location || "Remote"}
                        </span>
                        <span className="text-[10px] font-black uppercase tracking-wider px-2 py-1 bg-[#F5F5F5] text-[#525252] inline-flex items-center gap-1">
                            <Briefcase size={10} weight="bold" /> {job.employment_type}
                        </span>
                        <span className="text-[10px] font-black uppercase tracking-wider px-2 py-1 bg-[#F5F5F5] text-[#525252]">
                            {job.seniority}
                        </span>
                        {job.salary_range && (
                            <span className="text-[10px] font-black uppercase tracking-wider px-2 py-1 bg-[#ECFDF5] text-[#059669]">
                                {job.salary_range}
                            </span>
                        )}
                    </div>
                    <div className="space-y-4">
                        <div>
                            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">
                                The role
                            </p>
                            <p className="text-sm text-[#525252] whitespace-pre-wrap">
                                {job.description}
                            </p>
                        </div>
                        <div>
                            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">
                                You bring
                            </p>
                            <p className="text-sm text-[#525252] whitespace-pre-wrap">
                                {job.requirements}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Form side */}
                <div className="lg:col-span-3">
                    <div className="mb-3 bg-cobalt/5 border border-cobalt/30 p-3 text-xs text-[#525252] flex items-center justify-between gap-3 flex-wrap">
                        <span>
                            Want to track this application?{" "}
                            <Link
                                to={`/candidate/signup?job=${id}`}
                                data-testid="apply-signup-link"
                                className="font-bold text-cobalt hover:underline"
                            >
                                Create an account →
                            </Link>
                        </span>
                        <Link
                            to="/candidate/login"
                            data-testid="apply-signin-link"
                            className="font-bold text-ink hover:text-cobalt underline underline-offset-2"
                        >
                            Already have an account? Sign in
                        </Link>
                    </div>
                    <div className="bg-white border border-[#E5E5E5] p-5 sm:p-8">
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">
                            Apply now
                        </p>
                        <h2 className="font-heading text-2xl sm:text-3xl font-black tracking-tighter mb-1">
                            Tell us about you.
                        </h2>
                        <p className="text-sm text-[#525252] mb-6">
                            Drop your resume — we&apos;ll score your match instantly.
                        </p>

                        <form onSubmit={submit} className="space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {[
                                    ["name", "Full name", true],
                                    ["email", "Email", true],
                                    ["phone", "Phone", false],
                                    ["current_title", "Current title", false],
                                    ["years_experience", "Years experience", false],
                                ].map(([k, label, req]) => (
                                    <div key={k}>
                                        <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] mb-2 block">
                                            {label}
                                        </label>
                                        <input
                                            data-testid={`apply-${k}-input`}
                                            type={
                                                k === "years_experience"
                                                    ? "number"
                                                    : k === "email"
                                                      ? "email"
                                                      : k === "phone"
                                                        ? "tel"
                                                        : "text"
                                            }
                                            value={form[k]}
                                            required={req}
                                            placeholder={k === "phone" ? "+91 9876543210" : undefined}
                                            pattern={k === "phone" ? "^\\+?[0-9\\s\\-()\\.]{10,25}$" : undefined}
                                            minLength={k === "name" ? 2 : undefined}
                                            title={k === "phone" ? "Enter 10–15 digits; '+' and spaces/dashes are allowed" : undefined}
                                            onChange={(e) =>
                                                setForm({ ...form, [k]: e.target.value })
                                            }
                                            className="w-full bg-white border border-[#E5E5E5] px-4 py-2.5 text-base focus:outline-none focus:border-cobalt focus:ring-1 focus:ring-cobalt"
                                        />
                                    </div>
                                ))}
                                <div>
                                    <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] mb-2 block">
                                        Location
                                    </label>
                                    <AutocompleteInput
                                        testId="apply-location-input"
                                        field="location"
                                        staticOptions={LOCATION_SUGGESTIONS}
                                        value={form.location}
                                        onChange={(v) => setForm({ ...form, location: v })}
                                        placeholder="e.g. Bangalore, Remote"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] mb-2 block">
                                    Skills
                                </label>
                                <SkillsChipInput
                                    testId="apply-skills"
                                    endpoint="/public/suggestions/skills"
                                    value={skills}
                                    onChange={setSkills}
                                    placeholder="React, TypeScript, Node.js…"
                                />
                            </div>

                            <div>
                                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] mb-2 block">
                                    LinkedIn URL
                                </label>
                                <input
                                    type="url"
                                    value={form.linkedin}
                                    onChange={(e) => setForm({ ...form, linkedin: e.target.value })}
                                    className="w-full bg-white border border-[#E5E5E5] px-4 py-2.5 text-base focus:outline-none focus:border-cobalt"
                                />
                            </div>

                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252]">
                                        Resume
                                    </label>
                                    <label
                                        className="text-xs font-bold uppercase tracking-wider text-cobalt cursor-pointer inline-flex items-center gap-1 hover:underline"
                                        data-testid="apply-upload-pdf-label"
                                    >
                                        <UploadSimple size={12} weight="bold" />
                                        {pdfLoading ? "Parsing…" : "Upload resume"}
                                        <input
                                            data-testid="apply-pdf-input"
                                            type="file"
                                            accept=".pdf,.docx,.jpg,.jpeg,.png,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png"
                                            className="hidden"
                                            onChange={handlePdf}
                                            disabled={pdfLoading}
                                        />
                                    </label>
                                </div>
                                <textarea
                                    data-testid="apply-resume-input"
                                    value={typeof form.resume_text === "string" ? form.resume_text : ""}
                                    required
                                    minLength={30}
                                    rows={10}
                                    onChange={(e) =>
                                        setForm({ ...form, resume_text: e.target.value })
                                    }
                                    onPaste={smartPasteHandler((v) => setForm((f) => ({ ...f, resume_text: v })))}
                                    placeholder="Paste your resume or upload a file above (min 30 characters)…"
                                    className="w-full bg-white border border-[#E5E5E5] px-4 py-2.5 text-sm focus:outline-none focus:border-cobalt whitespace-pre-wrap"
                                />
                                <p className="mt-1 text-[10px] text-[#A3A3A3]">
                                    {form.resume_text.trim().length}/30 characters minimum · Upload PDF, DOCX, JPG or PNG — OCR handles scans.
                                </p>
                            </div>

                            {err && (
                                <div
                                    data-testid="apply-error"
                                    className="border border-[#DC2626] bg-[#FEF2F2] text-[#DC2626] text-sm px-4 py-3"
                                >
                                    {err}
                                </div>
                            )}

                            <button
                                data-testid="apply-submit-button"
                                type="submit"
                                disabled={submitting}
                                className="w-full bg-cobalt hover:bg-cobalt-dark disabled:opacity-60 text-white px-6 py-3.5 font-bold tracking-wide text-sm flex items-center justify-center gap-2 transition-colors"
                            >
                                {submitting ? "Submitting…" : "Submit application"}{" "}
                                <ArrowRight size={14} weight="bold" />
                            </button>

                            <p className="text-xs text-[#A3A3A3] text-center pt-2">
                                Powered by NaukriDaddy · Your resume is scored instantly by AI
                            </p>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}
